import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/* Customer self-service edits to their own `users` doc.
 *
 * THE ALLOW-LIST HERE MIRRORS THE FIRESTORE RULE, ON PURPOSE. `firestore.rules`
 * → match /users/{userId} lets a signed-in user update their own doc only while
 * `role`, `owner`, `permissions`, `familyId`, `email` and `studentId` are
 * unchanged — each pinned for a stated reason (email and familyId are SCOPE:
 * familyResolution walks an intake email to a family through a claimed
 * guardian's users doc, so a self-writable email captures another family's next
 * purchase). The rule is the wall; this list exists so the client never even
 * ATTEMPTS a pinned field, which turns a silent permission-denied into an
 * impossible state. If the rule changes, change this list in the same commit.
 */
export const EDITABLE_FIELDS = Object.freeze([
	'firstName',
	'lastName',
	'displayName',
	'phone',
	'address',
	'notificationPrefs',
]);

/* Studio mail a family may opt out of, and NOTHING ELSE.
 *
 * Derived from the sendEmail() audit table in `functions/index.js` (the block
 * above `let _stripe`): of the 22 call sites, only these two are studio-initiated
 * mail a parent could reasonably not want. Everything else is either the
 * parent's own action (inquiry, contact, purchase, waitlist) or transactional
 * (claim invite, payment receipt, password reset) — those must stay
 * unconditional, because an opt-out that swallows a receipt or a password reset
 * is a support incident, not a preference.
 *
 * ABSENT MEANS OPTED IN. Existing families have no `notificationPrefs` at all,
 * and defaulting a missing key to `false` would silently mute everyone the day
 * this ships. Every reader must treat undefined as true — see
 * `wantsEmail()` below and its server twin.
 */
export const NOTIFICATION_CHANNELS = Object.freeze([
	{
		key: 'announcements',
		label: 'Studio announcements',
		help: 'News, schedule changes and studio-wide updates.',
	},
	{
		key: 'lessonNotes',
		label: 'Lesson recaps',
		help: "Notes from your child's lesson, when a teacher sends them.",
	},
	{
		// 2026-10-14: the email the morning before each lesson
		// (functions/lessonReminders.js). Server twin: OPTIONAL_EMAIL_CHANNELS.
		key: 'lessonReminders',
		label: 'Lesson reminders',
		help: 'An email the morning before each lesson.',
	},
]);

/** Absent or malformed prefs mean opted IN. Mirrored server-side. */
export function wantsEmail(prefs, channelKey) {
	if (!prefs || typeof prefs !== 'object') return true;
	return prefs[channelKey] !== false;
}

/** Split a stored `displayName` into first/last for prefilling the form. */
export function splitDisplayName(displayName) {
	const parts = String(displayName || '')
		.trim()
		.split(/\s+/)
		.filter(Boolean);
	if (parts.length === 0) return { firstName: '', lastName: '' };
	return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

/* Read the signed-in customer's own profile, normalized for the form.
 *
 * `firstName`/`lastName` did not exist before this page: claimPortalAccount
 * writes a single `displayName`. So a first load falls back to splitting it,
 * which is also why the greeting read "Hi there" — CustomerDashboard reads
 * `firstName`, which nothing had ever written. */
export async function fetchCustomerProfile(uid) {
	const snap = await getDoc(doc(db, 'users', uid));
	if (!snap.exists()) return null;
	const data = snap.data() || {};
	const split = splitDisplayName(data.displayName);
	return {
		firstName: data.firstName || split.firstName,
		lastName: data.lastName || split.lastName,
		phone: data.phone || '',
		address: {
			line1: data.address?.line1 || '',
			line2: data.address?.line2 || '',
			city: data.address?.city || '',
			state: data.address?.state || '',
			postalCode: data.address?.postalCode || '',
		},
		notificationPrefs: {
			announcements: wantsEmail(data.notificationPrefs, 'announcements'),
			lessonNotes: wantsEmail(data.notificationPrefs, 'lessonNotes'),
		},
		email: data.email || '',
	};
}

/* Write the editable subset.
 *
 * `displayName` is written alongside first/last rather than replaced by them.
 * It is load-bearing elsewhere — TaskForm, TeacherDashboard, TaskDetail and two
 * Cloud Functions (captureTask, approveTriage) read it — so letting it drift
 * from the name the parent just typed would make staff surfaces show a stale
 * name forever. One typed name, three stored fields, written together. */
export async function saveCustomerProfile(uid, form) {
	const firstName = String(form.firstName || '').trim();
	const lastName = String(form.lastName || '').trim();
	const displayName = [firstName, lastName].filter(Boolean).join(' ');

	const payload = {
		firstName,
		lastName,
		phone: String(form.phone || '').trim(),
		address: {
			line1: String(form.address?.line1 || '').trim(),
			line2: String(form.address?.line2 || '').trim(),
			city: String(form.address?.city || '').trim(),
			state: String(form.address?.state || '').trim(),
			postalCode: String(form.address?.postalCode || '').trim(),
		},
		notificationPrefs: {
			announcements: form.notificationPrefs?.announcements !== false,
			lessonNotes: form.notificationPrefs?.lessonNotes !== false,
		},
		updatedAt: serverTimestamp(),
	};

	// Never blank a name that staff surfaces depend on: if the parent cleared
	// both fields, leave the stored displayName alone rather than writing ''.
	if (displayName) payload.displayName = displayName;

	await updateDoc(doc(db, 'users', uid), payload);
	return payload;
}
